/**
 * ZIP-321 payment URI builder.
 *
 * ZIP-321 is the Zcash payment request URI scheme — it lets a sender's
 * wallet read an address plus an optional amount, memo, and label out
 * of a single string (e.g. encoded into a QR code).
 *
 * We only implement the subset we need on the Receive screen: a single
 * payee (the user's UA), optional amount in ZEC, optional plain-text
 * memo (base64url-encoded per ZIP-321), and optional label. Multi-payee
 * URIs and parameter-indexed forms (e.g. `address.1=`) are not used.
 */

export interface Zip321Params {
  address: string;
  amount_zec?: number;
  memo?: string;
  label?: string;
}

/**
 * Build a ZIP-321 payment URI. Returns `zcash:<address>` with no query
 * string when no optional fields are provided, so the URI stays valid
 * even for plain "request my address" QRs.
 *
 * Example: `zcash:u1abc…?amount=0.5&memo=SGVsbG8`
 */
export function buildZip321Uri(p: Zip321Params): string {
  const parts: string[] = [];
  if (p.amount_zec != null && p.amount_zec > 0) {
    parts.push(`amount=${p.amount_zec}`);
  }
  if (p.memo) {
    // base64url(utf8(memo)) per ZIP-321: btoa wants binary string, so
    // we round-trip via encodeURIComponent + unescape to handle any
    // non-ASCII memo bytes correctly.
    const enc = btoa(unescape(encodeURIComponent(p.memo)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    parts.push(`memo=${enc}`);
  }
  if (p.label) {
    parts.push(`label=${encodeURIComponent(p.label)}`);
  }
  const query = parts.length ? `?${parts.join("&")}` : "";
  return `zcash:${p.address}${query}`;
}
